<template>
  <button
    ref="button"
    type="button"
    :disabled="disabled || loading"
    :aria-disabled="disabled || loading"
    class="group outline-secondary relative box-content flex shrink-0 grow-0 cursor-pointer! items-center justify-center overflow-hidden outline-offset-2 transition-all focus-visible:outline-[3px] disabled:cursor-default!"
    :class="[buttonHeight, buttonWidth, colorClasses, borderRadius]"
  >
    <BaseIcon v-if="!loading" :filled="selected" :size="iconSize" class="pointer-events-none">{{
      icon
    }}</BaseIcon>
    <BaseIndeterminateCircularProgressIndictor v-if="loading" class="h-full w-full" />
    <BaseStateLayer v-if="!disabled && !loading" :type="color" :force-pressed="forcePressed" />
  </button>
</template>

<script lang="ts" setup>
  import { computed, useTemplateRef } from 'vue'
  import BaseIcon from '../../icon/BaseIcon.vue'
  import BaseStateLayer from '../../state-layer/BaseStateLayer.vue'
  import type { ButtonShape } from '../button-shape'
  import type { ButtonColor } from '../button-color'
  import type { ButtonSize } from '../button-size'
  import { useMousePressed } from '@vueuse/core'
  import { useButtonSize } from '@/core/composables/use-button-size'
  import BaseIndeterminateCircularProgressIndictor from '../../progress-indicator/BaseIndeterminateCircularProgressIndictor.vue'

  interface Props {
    icon: string
    disabled?: boolean
    isSelectable?: boolean
    selected?: boolean
    size?: ButtonSize
    shape?: ButtonShape
    toggle?: boolean
    color?: Exclude<ButtonColor, 'text' | 'elevated'> | 'standard' | 'standardInverse'
    width?: 'default' | 'narrow' | 'wide'
    loading?: boolean
    forcePressed?: boolean
  }

  const {
    size = 'S',
    shape = 'round',
    width = 'default',
    color = 'standard',
    disabled,
    selected,
    toggle,
    forcePressed = false,
  } = defineProps<Props>()

  const { actualSize } = useButtonSize(size)

  const buttonHeight = computed(() => {
    if (color == 'outlined') {
      if (actualSize.value == 'XS') {
        return 'h-[30px]'
      }
      if (actualSize.value == 'S') {
        return 'h-[38px]'
      }
      if (actualSize.value == 'M') {
        return 'h-[54px]'
      }
      if (actualSize.value == 'L') {
        return 'h-[94px]'
      }
      return 'h-[134px]'
    }
    if (actualSize.value == 'XS') {
      return 'h-8'
    }
    if (actualSize.value == 'S') {
      return 'h-10'
    }
    if (actualSize.value == 'M') {
      return 'h-14'
    }
    if (actualSize.value == 'L') {
      return 'h-24'
    }
    return 'h-[136px]'
  })

  const buttonWidth = computed(() => {
    if (actualSize.value == 'XS') {
      if (width === 'narrow') {
        return 'w-7'
      }
      if (width === 'wide') {
        return 'w-10'
      }
      return 'w-8'
    }
    if (actualSize.value == 'S') {
      if (width === 'narrow') {
        return 'w-8'
      }
      if (width === 'wide') {
        return 'w-[52px]'
      }
      return 'w-10'
    }
    if (actualSize.value == 'M') {
      if (width === 'narrow') {
        return 'w-12'
      }
      if (width === 'wide') {
        return 'w-[72px]'
      }
      return 'w-14'
    }
    if (actualSize.value == 'L') {
      if (width === 'narrow') {
        return 'w-16'
      }
      if (width === 'wide') {
        return 'w-[128px]'
      }
      return 'w-24'
    }
    if (width === 'narrow') {
      return 'w-[104px]'
    }
    if (width === 'wide') {
      return 'w-[184px]'
    }
    return 'w-[136px]'
  })

  const iconSize = computed(() => {
    if (actualSize.value == 'XS') {
      return '20'
    }
    if (actualSize.value == 'S') {
      return '24'
    }
    if (actualSize.value == 'M') {
      return '24'
    }
    if (actualSize.value == 'L') {
      return '32'
    }
    return '40'
  })

  const button = useTemplateRef<HTMLButtonElement>('button')
  const { pressed } = useMousePressed({ target: button })

  const borderRadius = computed(() => {
    if (pressed.value || forcePressed) {
      if (actualSize.value == 'XS') {
        return 'rounded-[8px]'
      }
      if (actualSize.value == 'S') {
        return 'rounded-[8px]'
      }
      if (actualSize.value == 'M') {
        return 'rounded-[12px]'
      }
      if (actualSize.value == 'L') {
        return 'rounded-[16px]'
      }
      return 'rounded-[16px]'
    }
    if ((selected && shape === 'square') || (shape === 'round' && !selected)) {
      if (actualSize.value == 'XS') {
        return 'rounded-[16px]'
      }
      if (actualSize.value == 'S') {
        return 'rounded-[20px]'
      }
      if (actualSize.value == 'M') {
        return 'rounded-[28px]'
      }
      if (actualSize.value == 'L') {
        return 'rounded-[48px]'
      }
      return 'rounded-[68px]'
    }
    if (actualSize.value == 'XS') {
      return 'rounded-[12px]'
    }
    if (actualSize.value == 'S') {
      return 'rounded-[12px]'
    }
    if (actualSize.value == 'M') {
      return 'rounded-[16px]'
    }
    if (actualSize.value == 'L') {
      return 'rounded-[28px]'
    }
    return 'rounded-[28px]'
  })

  const colorClasses = computed(() => {
    if (color == 'filled') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (!toggle || selected) {
          return 'bg-primary text-on-primary hover:shadow-1'
        } else {
          return 'bg-surface-container text-on-surface-variant'
        }
      }
    }
    if (color == 'filledInverse') {
      if (disabled) {
        return 'bg-on-primary/[10%] text-on-primary/[38%]'
      } else {
        if (!toggle || selected) {
          return 'bg-on-primary text-primary hover:shadow-1'
        } else {
          return 'bg-primary-container text-on-primary-container'
        }
      }
    }
    if (color == 'filledTertiary') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (!toggle || selected) {
          return 'bg-tertiary text-on-tertiary hover:shadow-1'
        } else {
          return 'bg-surface-container text-on-surface-variant'
        }
      }
    }
    if (color == 'tonal') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (selected) {
          return 'bg-secondary text-on-secondary hover:shadow-1'
        } else {
          return 'bg-secondary-container text-on-secondary-container hover:shadow-1'
        }
      }
    }
    if (color == 'tonalPrimary') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (selected) {
          return 'bg-primary text-on-primary hover:shadow-1'
        } else {
          return 'bg-primary-container text-on-primary-container hover:shadow-1'
        }
      }
    }
    if (color == 'tonalTertiary') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (selected) {
          return 'bg-tertiary text-on-tertiary hover:shadow-1'
        } else {
          return 'bg-tertiary-container text-on-tertiary-container hover:shadow-1'
        }
      }
    }
    if (color == 'outlined') {
      if (disabled) {
        return 'border border-outline-variant bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (selected) {
          return 'border bg-inverse-surface text-inverse-on-surface'
        } else {
          return 'border border-outline-variant text-on-surface-variant'
        }
      }
    }
    if (color == 'standardInverse') {
      if (disabled) {
        return 'text-on-primary/[38%]'
      } else {
        if (selected) {
          return 'text-on-primary'
        } else {
          return 'text-on-primary'
        }
      }
    }
    if (disabled) {
      return 'text-on-surface/[38%]'
    } else {
      if (selected) {
        return 'text-primary'
      } else {
        return 'text-on-surface-variant'
      }
    }
  })
</script>
